import { useState } from 'react';
import type { InvoiceTrip } from '@coreledger/shared-types';

interface Props {
  trips: InvoiceTrip[];
  onChange: (trips: InvoiceTrip[]) => void;
}

function emptyTrip(): InvoiceTrip {
  return { vehicleNumber: '', fromCity: '', toCity: '', driverName: '', weight: '', freightAmount: 0, lrNumber: '', lrDate: new Date().toISOString().slice(0, 10) };
}

export function TripsEditor({ trips, onChange }: Props) {
  const [expandedTrips, setExpandedTrips] = useState<Set<number>>(new Set());

  function setTrip(ti: number, k: keyof InvoiceTrip, val: string | number) {
    const next = [...trips];
    next[ti] = { ...next[ti], [k]: val };
    onChange(next);
  }

  function addTrip() {
    onChange([...trips, emptyTrip()]);
  }

  function removeTrip(ti: number) {
    onChange(trips.filter((_, i) => i !== ti));
  }

  function toggleExpand(ti: number) {
    setExpandedTrips(s => { const n = new Set(s); n.has(ti) ? n.delete(ti) : n.add(ti); return n; });
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Trips</span>
        <button className="btn btn-ghost btn-sm" onClick={addTrip}>+ Add Trip</button>
      </div>
      {trips.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '0.5rem 0' }}>No trips added. Click "+ Add Trip" to add individual trip details.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>#</th><th>Vehicle</th><th>LR #</th><th>LR Date</th><th>From</th><th>To</th><th>Driver</th><th>Weight</th><th>Container</th><th>Client Inv #</th><th style={{ textAlign: 'right' }}>Freight (₹)</th><th></th>
              </tr>
            </thead>
            <tbody>
              {trips.map((trip, ti) => {
                const isConsigneeExpanded = expandedTrips.has(ti);
                return (
                  <>
                    <tr key={`trip-${ti}`}>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{ti + 1}</td>
                      <td><input value={trip.vehicleNumber ?? ''} onChange={e => setTrip(ti, 'vehicleNumber', e.target.value.toUpperCase())} placeholder="MH12AB1234" style={{ width: 110 }} /></td>
                      <td><input value={trip.lrNumber ?? ''} onChange={e => setTrip(ti, 'lrNumber', e.target.value)} placeholder="LR-001" style={{ width: 90 }} /></td>
                      <td><input type="date" value={trip.lrDate ?? ''} onChange={e => setTrip(ti, 'lrDate', e.target.value)} style={{ width: 120 }} /></td>
                      <td><input value={trip.fromCity ?? ''} onChange={e => setTrip(ti, 'fromCity', e.target.value)} placeholder="Mumbai" style={{ width: 90 }} /></td>
                      <td><input value={trip.toCity ?? ''} onChange={e => setTrip(ti, 'toCity', e.target.value)} placeholder="Delhi" style={{ width: 90 }} /></td>
                      <td><input value={trip.driverName ?? ''} onChange={e => setTrip(ti, 'driverName', e.target.value)} placeholder="Name" style={{ width: 100 }} /></td>
                      <td><input value={trip.weight ?? ''} onChange={e => setTrip(ti, 'weight', e.target.value)} placeholder="5 MT" style={{ width: 70 }} /></td>
                      <td><input value={trip.containerSize ?? ''} onChange={e => setTrip(ti, 'containerSize', e.target.value)} placeholder="20ft" style={{ width: 60 }} /></td>
                      <td><input value={trip.invoiceRef ?? ''} onChange={e => setTrip(ti, 'invoiceRef', e.target.value)} placeholder="Client's inv #" style={{ width: 100 }} /></td>
                      <td><input type="number" min={0} step={0.01} value={trip.freightAmount ?? ''} onChange={e => setTrip(ti, 'freightAmount', parseFloat(e.target.value) || 0)} style={{ width: 90, textAlign: 'right' }} /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" onClick={() => toggleExpand(ti)} style={{ background: 'none', border: 'none', fontSize: '0.75rem', color: 'var(--primary)', cursor: 'pointer', padding: '0 0.2rem' }} title="Per-trip consignee">
                          {isConsigneeExpanded ? '▴' : '▾'}
                        </button>
                        <button onClick={() => removeTrip(ti)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: '1rem', cursor: 'pointer', padding: '0 0.25rem' }}>×</button>
                      </td>
                    </tr>
                    {isConsigneeExpanded && (
                      <tr key={`trip-consignee-${ti}`} style={{ background: 'var(--surface-alt)' }}>
                        <td colSpan={11} style={{ padding: '0.5rem 0.75rem' }}>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', alignSelf: 'center' }}>Consignee</span>
                            <input value={trip.consignee ?? ''} onChange={e => setTrip(ti, 'consignee', e.target.value)} placeholder="Consignee name" style={{ flex: 2, minWidth: 140 }} />
                            <input value={trip.consigneeGstin ?? ''} onChange={e => setTrip(ti, 'consigneeGstin', e.target.value.toUpperCase())} placeholder="GSTIN" maxLength={15} style={{ flex: 1, minWidth: 130, fontFamily: 'monospace', textTransform: 'uppercase' }} />
                            <input value={trip.consigneeAddress ?? ''} onChange={e => setTrip(ti, 'consigneeAddress', e.target.value)} placeholder="Delivery address" style={{ flex: 3, minWidth: 180 }} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
            {trips.length > 1 && (
              <tfoot>
                <tr>
                  <td colSpan={10} style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-2)' }}>Total Freight</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{trips.reduce((s, t) => s + (t.freightAmount ?? 0), 0).toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
